import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subscription } from 'rxjs';

import { ApiError, DataTablesService, MessageService } from '../../../core';

import { take } from 'rxjs/operators';
import { AtividadeOptions } from '../atividade-options';
import { AtividadeSearchAtividade, AtividadeSearchTotal } from '../atividade-search';
import { AtividadeSearchOrderBy } from '../atividade-search-orderby';
import { AtividadeUpdateConcluida } from '../concluida';
import { AtividadeFilter } from '../filter';
import { AtividadeAuthGuardService, AtividadeConcluidaHttpService, AtividadeSearchHttpService } from '../services';
import { AtividadeTipoMotivoConclusaoBloqueadaPipe, AtividadeTipoPipe, isTipoClienteRevisao } from '../tipo';
import { CalendarView } from 'angular-calendar';
import {
    endOfDay,
    endOfMonth,
    endOfWeek,
    startOfDay,
    startOfMonth,
    startOfWeek,
    isSameDay,
    isSunday,
    isBefore,
    differenceInDays,
    differenceInHours
} from 'date-fns';
import { isOrigemRetorno } from '../atividade-origem';

@Component({
    selector: 'sgt-atividade-list',
    templateUrl: './atividade-list.component.html',
    styleUrls: ['./atividade-list.component.less']
})
export class AtividadeListComponent implements OnInit, OnDestroy {
    /** Subscriptions das pesquisas. Utilizado para realizar o unsubscribe e cancelar o retorno de requests de pesquissa pendentes antes de iniciar novos requests */
    private searchSubs$: Subscription;

    private dataTable: any;

    @Input() filter: AtividadeFilter;
    /** Se deve ocultar a coluna do cliente */
    @Input() hiddenColumnCliente = false;
    @Input() canDelete = true; // Se pode excluir as atividades
    @Input() canOpenCliente: boolean; // Se pode abrir o cliente através atividade

    atividades: AtividadeSearchAtividade[];
    atividadesTotal: AtividadeSearchTotal;

    @Output() edit = new EventEmitter<AtividadeSearchAtividade>();
    @Output() delete = new EventEmitter<AtividadeSearchAtividade>();
    @Output() openCliente = new EventEmitter<AtividadeSearchAtividade>();

    locale = 'pt-BR';
    viewDate: Date;
    view: CalendarView = CalendarView.Month;
    isPeriodoView = false;
    CalendarView = CalendarView;

    constructor(private messageService: MessageService,
                private dataTablesService: DataTablesService,
                private datePipe: DatePipe,
                private atividadeTipoPipe: AtividadeTipoPipe,
                private atividadeTipoMotivoConclusaoBloqueadaPipe: AtividadeTipoMotivoConclusaoBloqueadaPipe,
                private atividadeAuthGuardService: AtividadeAuthGuardService,
                private atividadeSearchHttpService: AtividadeSearchHttpService,
                private atividadeConcluidaHttpService: AtividadeConcluidaHttpService) { }

    ngOnInit() {
        if (!this.filter.diaPrazoInicial || !this.filter.diaPrazoFinal) {
            this.onViewDateChange(new Date());
        } else {
            this.viewDate = this.filter.diaPrazoInicial;
        }
    }

    setView(view: CalendarView, isPeriodoView: boolean) {
        this.view = view;
        this.isPeriodoView = isPeriodoView;
        this.onViewDateChange(this.viewDate);
    }

    onViewDateChange(newViewDate: Date) {
        this.viewDate = newViewDate;

        if (!this.isPeriodoView) {
            let startDate: Date;
            let endDate: Date;

            if (this.view === CalendarView.Day) { // Day
                startDate = startOfDay(newViewDate);
                endDate = endOfDay(newViewDate);
            } else if (this.view === CalendarView.Week) { // Week
                startDate = startOfWeek(newViewDate);
                endDate = endOfWeek(newViewDate);
            } else { // Month
                startDate = startOfMonth(newViewDate);
                endDate = endOfMonth(newViewDate);
            }

            this.filter.diaPrazoInicial = startDate;
            this.filter.diaPrazoFinal = endDate;
        }

        this.search();
    }

    onPeriodoChange() {
        this.search();
    }

    search() {
        // Realizando unsubscribe dos subscriptions atuais e criando um novo para as próximas pesquisas
        this.searchSubs$?.unsubscribe();
        this.searchSubs$ = new Subscription();

        // Obtendo os totais das atividades
        this.getSearchTotais()
            .then(() => {
                if (!this.dataTable) { // Table ainda não criada
                    this.createTable();
                } else { // Table já criada, somente damos um reload
                    this.dataTable.ajax.reload();
                }
            })
            .catch(() => {});
    }

    private getSearchTotais(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.searchSubs$.add(this.atividadeSearchHttpService.total(this.filter)
                .subscribe(
                    total => {
                        this.atividadesTotal = total;
                        resolve();
                    },
                    (error: ApiError) => {
                        this.messageService.error(error.message, 'Erro ao carregar os totais das atividades', error);
                        reject(error);
                    }
                )
            );
        });
    }

    /** Cria a dataTable com pesquisa dos clientes sob-demanda */
    private createTable() {
        // const columns: DataTablesColumnSettings[] = [
        const columns: any[] = [
            {
                data: null,
                className: 'col-options',
                render: (data: AtividadeSearchAtividade) => {
                    if (!data) {
                        return null;
                    }

                    let template = '';

                    const options = new AtividadeOptions();
                    // Editar
                    options.canEdit = true;
                    // Excluir
                    options.canDelete = this.canDelete && this.atividadeAuthGuardService.canDelete(data.lancamento);
                    // Abrir o cliente da atividade
                    options.canOpenCliente = this.canOpenCliente;

                    if (options.hasAnyOption()) {
                        template += `
                        <span class="btn-group dropup table-options">
                            <button type="button" title="Opções" class="btn btn-link btn-options dropdown-toggle" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                                <i class="fas fa-ellipsis-h" aria-hidden="true"></i>
                            </button>
                            <div class="dropdown-menu">
                        `;

                        // Editar
                        if (options.canEdit) {
                            template += `
                                <a class="dropdown-item" id="edit">
                                    <i class="fas fa-pencil-alt" aria-hidden="true"></i> Editar
                                </a>
                            `;
                        }
                        // Excluir
                        if (options.canDelete) {
                            template += `
                                <a class="dropdown-item dropdown-item-delete" id="delete">
                                    <i class="far fa-trash-alt" aria-hidden="true"></i> Excluir
                                </a>
                            `;
                        }
                        // Abrir cliente
                        if (options.canOpenCliente) {
                            if (options.getCountOptions() > 1) {
                                template += `
                                    <div class="dropdown-divider"></div>
                                `;
                            }
                            template += `
                                <a class="dropdown-item" id="openCliente">
                                    Abrir Cliente
                                </a>
                            `;
                        }
                    }

                    template += `
                        </div>
                    </span>
                    `;

                    return template;
                },
                createdCell: (td, cellData) => {
                    const atividade = cellData as AtividadeSearchAtividade;
                    const myself = this;

                    const dropdown = jQuery(td).find('.btn-group');
                    // Ao abrir o dropdown, adicionamos ele no body para exibi-lo corretamente na table
                    dropdown.on('show.bs.dropdown', function() {
                        jQuery('body').append(dropdown.css({
                                position: 'absolute',
                                left: dropdown.offset().left,
                                top: dropdown.offset().top
                            }).detach());
                    });
                    // Ao fechar o dropdown, adicionamos ele novamente a coluna das opções na table
                    dropdown.on('hidden.bs.dropdown', function() {
                        jQuery(td).append(dropdown.css({
                            position: 'relative',
                            left: 'auto',
                            top: 'auto'
                        }).detach());
                    });

                    // Opções do dropdown
                    dropdown.on('click', '.dropdown-item', function() {
                        if (this.id === 'edit') {
                            myself.editClick(atividade);
                        } else if (this.id === 'delete') {
                            myself.deleteClick(atividade);
                        } else if (this.id === 'openCliente') {
                            myself.openClienteClick(atividade);
                        }
                    });
                }
            },
            {
                data: null,
                className: 'col-status text-center',
                render: (data, type, row, meta) => {
                    const atividade = data as AtividadeSearchAtividade;

                    // Conclusão bloqueada
                    if (atividade.conclusaoBloqueada) {
                        return `
                            <div title="${this.atividadeTipoMotivoConclusaoBloqueadaPipe.transform(atividade.tipo)}">
                                <i class="fas fa-lock"></i>
                            </div>
                        `
                    }

                    return `
                        <div class="custom-control custom-checkbox">
                            <input type="checkbox" class="custom-control-input" id="atividadeConcluida_${atividade?.id}" ${atividade?.concluida ? 'checked' : ''}>
                            <label class="custom-control-label" for="atividadeConcluida_${atividade?.id}"></label>
                        </div>
                    `;
                },
                createdCell: (td, cellData, rowData, row, col) => {
                    const atividade = cellData as AtividadeSearchAtividade;
                    // Concluída
                    if (atividade?.concluida) {
                        jQuery(td).addClass('concluida');
                    }
                    // Atrasada
                    if (atividade?.atrasada) {
                        jQuery(td).addClass('atrasada');
                    }

                    // Somente quando a conclusão não estiver bloqueada
                    if (!atividade.conclusaoBloqueada) {
                        const myself = this;
                        jQuery(td).find('input[type=checkbox]').on('click', function() {
                            atividade.concluida = !atividade.concluida;
                            myself.updateConcluida(atividade)
                                .then(() => {
                                    // Adicionando a css class conforme o status de conclusão da atividade após o update
                                    jQuery(td).removeClass('concluida').removeClass('atrasada');

                                    // Concluída
                                    if (atividade?.concluida) {
                                        jQuery(td).addClass('concluida');
                                    }
                                    // Atrasada
                                    if (atividade?.atrasada) {
                                        jQuery(td).addClass('atrasada');
                                    }
                                })
                                .catch(() => {});
                        });
                    }
                }
            },
            {
                data: null,
                className: 'col-flags text-center',
                render: (data, type, row, meta) => {
                    const atividade = data as AtividadeSearchAtividade;

                    let flags = '';
                    // Retorno
                    if (isOrigemRetorno(atividade.origem)) {
                        flags += `<i class="fas fa-retweet" title="Atividade de Retorno"></i>`;
                    }
                    // Sinalizado
                    if (atividade?.sinalizado) {
                        flags += `<i class="fas fa-flag atividade-flag atividade-flag-sinalizado" title="Sinalizado"></i>`;
                    }
                    // Importante
                    if (atividade?.importante) {
                        flags += `<i class="fas fa-exclamation-triangle atividade-flag atividade-flag-importante" title="Importante"></i>`;
                    }

                    return flags;
                },
            },
            {
                data: 'id',
                className: 'col-id text-right',
                serverOrderByColumn: AtividadeSearchOrderBy.ID,
                createdCell: (td, cellData, rowData, row, col) => td.title = cellData ? `#${cellData}` : ''
            },
            {
                data: null,
                className: 'col-tipo text-center',
                serverOrderByColumn: AtividadeSearchOrderBy.Tipo,
                render: (data: AtividadeSearchAtividade) => {
                    if (!data) {
                        return null;
                    }

                    let template = this.atividadeTipoPipe.transform(data.tipo);
                    // Revisão de cliente com cadastro novo
                    if (isTipoClienteRevisao(data.tipo) && data.clienteCadastroNovo) {
                        template += `
                            <span class="badge badge-primary" title="Cadastro Novo">
                                <i class="fas fa-asterisk"></i>
                            </span>
                        `;
                    }

                    return template;
                }
            },
            {
                data: 'dataEmissao',
                className: 'col-data-emissao text-center',
                serverOrderByColumn: AtividadeSearchOrderBy.DataEmissao,
                render: (data, type, row, meta) => {
                    if (!data) {
                        return null;
                    }

                    return `${this.datePipe.transform(data, 'dd/MM/yyyy')} ${this.datePipe.transform(data, 'HH:mm')}`;
                }
            },
            {
                data: null,
                className: 'col-data-prazo text-center',
                serverOrderByColumn: AtividadeSearchOrderBy.DiaPrazoESequencia,
                render: (data, type, row, meta) => {
                    const atividade = data as AtividadeSearchAtividade;

                    let prazo = '';
                    // Dia prazo
                    if (atividade?.diaPrazo) {
                        prazo += this.datePipe.transform(atividade?.diaPrazo, 'dd/MM/yyyy');

                        // Hora prazo
                        if (atividade?.horaPrazo) {
                            prazo += ` ${this.datePipe.transform(atividade?.horaPrazo, 'HH:mm')}`;
                        }
                    }

                    return prazo;
                }
            },
            {
                data: 'dataConclusao',
                className: 'col-data-conclusao text-center',
                serverOrderByColumn: AtividadeSearchOrderBy.DataConclusao,
                render: (data, type, row, meta) => {
                    if (!data) {
                        return null;
                    }

                    return `${this.datePipe.transform(data, 'dd/MM/yyyy')} ${this.datePipe.transform(data, 'HH:mm')}`;
                }
            },
            {
                data: 'nomeFantasiaCliente',
                className: 'col-cliente',
                serverOrderByColumn: AtividadeSearchOrderBy.NomeFantasiaCliente,
                createdCell: (td, cellData, rowData, row, col) => td.title = cellData ? cellData : ''
            },
            {
                data: 'titulo',
                className: 'col-titulo',
                serverOrderByColumn: AtividadeSearchOrderBy.Titulo,
                createdCell: (td, cellData, rowData, row, col) => td.title = cellData ? cellData : ''
            },
            {
                data: 'comentario',
                className: 'col-comentario',
                serverOrderByColumn: AtividadeSearchOrderBy.Comentario,
                createdCell: (td, cellData, rowData, row, col) => td.title = cellData ? cellData : ''
            },
            {
                data: 'nomeUsuarioDestino',
                className: 'col-responsavel',
                serverOrderByColumn: AtividadeSearchOrderBy.NomeUsuarioDestino,
                createdCell: (td, cellData, rowData, row, col) => td.title = cellData ? cellData : ''
            }
        ];

        const columnDefs: any[] = [
            {orderable: false, targets: [0, 1, 2]}
        ]
        // Ocultar coluna do cliente
        if (this.hiddenColumnCliente) {
            columnDefs.push({visible:  false, targets: 7})
        }

        this.dataTablesService.showTable('#tableAtividadeList', this.dataTable, {
            columnDefs,
            columns,
            order: [[5, 'desc']], // dia prazo desc
            searching: false,
            info: false,
            serverSide: true,
            processing: true,
            deferRender: true,
            scroller: true,
            scrollX: true,
            ajax: (data, callback, settings) => {
                // Offset e Limit
                const offset = data.start;
                const limit = data.length;
                // Order By
                const order = data?.order[0];
                const orderColumn = columns[order?.column];
                const orderBy = orderColumn?.serverOrderByColumn;
                const orderByDir = order?.dir;

                this.searchSubs$.add(this.atividadeSearchHttpService.atividades(this.filter, offset, limit, orderBy, orderByDir)
                    .subscribe(
                        atividades => {
                            callback({recordsTotal: this.atividadesTotal?.quantidadeAtividades, recordsFiltered: this.atividadesTotal?.quantidadeAtividades, data: atividades});
                        },
                        (error: ApiError) => {
                            this.messageService.error(error.message, 'Erro ao pesquisar as atividades', error);
                            callback({recordsTotal: 0, recordsFiltered: 0, data: []});
                        }
                    )
                );
            }
        }).then(dataTable => {
            this.dataTable = dataTable
        })
        .catch(() => {});
    }

    /** Atualiza a dataTable */
    refresh() {
        this.dataTable?.draw('page');
        this.getSearchTotais()
            .then(() => {})
            .catch(() => {});
    }

    getNomeUsuarioAbreviado(nomeUsuario: string): string {
        if (!nomeUsuario) {
            return '';
        }

        const nomes = nomeUsuario.split(' ');
        if (nomes.length > 0) {
            return nomes[0];
        }
        return nomeUsuario;
    }

    /** Emite evento para editar a atividade */
    editClick(atividade: AtividadeSearchAtividade) {
        this.edit.emit(atividade);
    }

    /** Emite evento para excluir a atividade */
    deleteClick(atividade: AtividadeSearchAtividade) {
        if (!this.canDelete) {
            return;
        }

        this.delete.emit(atividade);
    }

    /** Emite evento para abrir o cliente da atividade */
    openClienteClick(atividade: AtividadeSearchAtividade) {
        this.openCliente.emit(atividade);
    }

     /** Realiza a atualização na atividade marcando a mesma como concluída ou não */
     updateConcluida(entity: AtividadeSearchAtividade): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            if (entity.conclusaoBloqueada) {
                reject();
            }

            const entityUpdate = new AtividadeUpdateConcluida(entity.id, entity.concluida);
            this.atividadeConcluidaHttpService.updateConcluida(entityUpdate)
                .pipe(
                    take(1)
                )
                .subscribe(
                    response => {
                        entity.atrasada = response.atrasada;
                        resolve();
                    },
                    (error: ApiError) => {
                        this.messageService.error(error.message, 'Ocorreu um erro ao atualizar a conclusão da atividade', error);
                        reject();
                    }
                );
        });
    }

    ngOnDestroy() {
        // Realizando unsubscribe dos subscriptions das pesquisas
        this.searchSubs$?.unsubscribe();
    }
}
